import React, { useEffect, useRef, useState, FC, useCallback } from "react";
import {
  Application,
  Container,
  Texture,
  Assets,
  Graphics,
  Text,
  BitmapText,
  TextStyle,
  Rectangle,
} from "pixi.js";
import {
  BackdropBlurFilter,
  AdjustmentFilter,
  AdvancedBloomFilter,
  OutlineFilter,
} from "pixi-filters";
import { Viewport } from "pixi-viewport";
import chalk from "chalk";
import { useLocation, useParams } from "wouter";
import { IArtworkSprite, SortingArtwork } from "../../interfaces";

import { gsap } from "gsap";
import { PixiPlugin } from "gsap/PixiPlugin";
import ZoomControl, { ZoomControlSize } from "../../components/ZoomControl";
import { addArtworkSpriteEvents, createArtworkSprite } from "../../utils/pixi";

import Loading from "../../components/Loading";
import { isDebugMode } from "../../config";
import { TimelineSimulation } from "../../simulation/TimelineSimulation";
import _, { debounce } from "lodash";
import BackToCollectionButton from "../../components/buttons/BackToCollectionButton";
import ArtworkDetailView, { ArtworkDetailViewRef } from "../ArtworkDetail";
import { useStore } from "../../store/store";
gsap.registerPlugin(PixiPlugin);

const DOUBLE_CLICK_THRESHOLD = 300; // 300 ms
const MIN_ZOOM = 0.001;
const MAX_ZOOM = 8;

/**
 * Extracts the file extension from a given URL.
 * Returns the extension without the dot, e.g., "png", "jpeg".
 * If no extension is found, returns an empty string.
 * @param url The URL string to extract the extension from.
 */
const getFileExtension = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const lastDotIndex = pathname.lastIndexOf(".");
    if (lastDotIndex === -1) return "";
    return pathname.substring(lastDotIndex + 1).toLowerCase();
  } catch (error) {
    console.error("Invalid URL:", url);
    return "";
  }
};
const loadJSON = async (url: string) => {
  const response = await fetch(url);
  return await response.json();
};

const TimelineView: FC = () => {
  const [, navigate] = useLocation();
  const params = useParams();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pixiAppRef = useRef<Application | null>(null);
  const viewportRef = useRef<Viewport | null>(null);
  const hasFetchedData = useRef(false);
  const simulationRef = useRef<TimelineSimulation | null>(null);
  let lastClickTimeRef = useRef<number>(0);
  const artworkContainerRef = useRef<Container>(new Container());
  const debugLayerRef = useRef<Container>(new Container());
  const timelineContainerRef = useRef<Container>(new Container());

  // --------------------
  // Store Hooks
  // --------------------
  const { combinedTextures, setCombinedTextures, clearCombinedTextures } =
    useStore();

  const [texturesLoaded, setTexturesLoaded] = useState<boolean>(false);
  const [spritesSetup, setSpritesSetup] = useState<boolean>(false);
  const [isDataFetched, setIsDataFetched] = useState<boolean>(false);
  const artworkRef = useRef<ArtworkDetailViewRef>(null);
  const [uuid, setUuid] = useState<string | null>(null);
  const [artworks, setArtworks] = useState<SortingArtwork[]>([]);
  const [artworkSprites, setArtworkSprites] = useState<IArtworkSprite[]>([]);
  const [isRendered, setIsRendered] = useState<boolean>(false);

  const [dimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [worldDimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const [isAnimating] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasSimulationFinished, setHasSimulationFinished] =
    useState<boolean>(false);

  const [timelineRange, setTimelineRange] = useState<{
    minYear: number;
    maxYear: number;
  }>({
    minYear: 0,
    maxYear: 0,
  });

  const hasData = artworks.length !== 0;

  useEffect(() => {
    artworkContainerRef.current.zIndex = 11;
    timelineContainerRef.current.zIndex = 20;
    if (!hasFetchedData.current) {
      console.group("Sequence");
      console.info(chalk.cyan("[SEQ1]") + "Fetching data");
      fetchArtworkTimeline();
      hasFetchedData.current = true;
    }
  }, []);

  useEffect(() => {
    if (hasData && isDataFetched && !texturesLoaded && !simulationRef.current) {
      console.info(chalk.cyan("[SEQ2]") + "Loading textures");
      (async () => {
        await loadArtworkTextures();
      })();
    }
  }, [hasData, isDataFetched, texturesLoaded]);

  useEffect(() => {
    if (texturesLoaded) {
      console.info(chalk.cyan("[SEQ3]") + "Setting up sprites");
      (async () => {
        await setupSprites();
      })();
    }
  }, [texturesLoaded]);

  useEffect(() => {
    if (spritesSetup) {
      console.info(chalk.cyan("[SEQ4]") + "Running simulation");
      (async () => {
        await runSimulation();
      })();
    }
  }, [spritesSetup]);

  useEffect(() => {
    if (
      texturesLoaded &&
      spritesSetup &&
      hasSimulationFinished &&
      !pixiAppRef.current
    ) {
      console.info(chalk.cyan("[SEQ5]") + "Render pixi");
      console.groupEnd();
      (async () => {
        await renderPixi();
      })();
    }
  }, [texturesLoaded, hasSimulationFinished, spritesSetup, pixiAppRef.current]);

  useEffect(() => {
    const handleResize = () => {
      if (viewportRef.current && pixiAppRef.current) {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;
        pixiAppRef.current.renderer.resize(newWidth, newHeight);
        viewportRef.current.resize(newWidth, newHeight, newWidth, newHeight);
        adjustViewport();
        updateTimeline();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const cleanup = useCallback(() => {
    if (simulationRef.current) {
      simulationRef.current.instance.stop();
      simulationRef.current = null;
    }

    if (pixiAppRef.current) {
      pixiAppRef.current.destroy(true, {
        children: true,
        texture: true,
      });
      pixiAppRef.current = null;
    }

    if (viewportRef.current) {
      viewportRef.current.destroy();
      viewportRef.current = null;
    }
  }, []);

  const fetchArtworkTimeline = async () => {
    setIsDataFetched(true);
    setLoadingProgress(10);
    try {
      const response = await fetch(
        `https://admin.collectieconnectie.nl/api/v1/artworks/timeline`,
      );
      const result = await response.json();
      transformArtworkData(result);
      setLoadingProgress(30);
    } catch (error) {
      console.error("Error fetching artworks data:", error);
      setArtworks([]);
      setIsRendered(true);
      setLoadingProgress(100);
      setIsLoading(false);
    }
  };

  const transformArtworkData = (response: any) => {
    let collection = response.collection;
    if (collection.length !== 0) {
      const filtered = _.filter(
        collection,
        (artwork) => artwork.details.start_year !== null,
      )
        .map((artwork) => {
          return {
            uuid: artwork.uuid,
            name: artwork.title,
            collection: artwork.collection,
            imageUrl: artwork.smallest_image_url,
            startYear: artwork.details.start_year,
          };
        })
        .sort((a, b) => a.startYear - b.startYear);
      setArtworks(filtered);
      setLoadingProgress(50);

      const years = filtered.map((art) => art.startYear).filter(Boolean);
      const minYear = Math.min(...years);
      const maxYear = Math.max(...years);
      setTimelineRange({ minYear, maxYear });
    } else {
      setArtworks([]);
      setIsRendered(true);
      setLoadingProgress(100);
      setIsLoading(false);
    }
  };

  const runSimulation = useCallback(async () => {
    if (!artworks.length && !artworkSprites.length && !simulationRef.current)
      return;
    const simulation = new TimelineSimulation(
      artworks,
      artworkSprites,
      worldDimensions,
    );
    simulation.onSimulationEnd = async () => {
      setLoadingProgress(60);
      simulationRef.current = simulation;
      setLoadingProgress(90);
      setHasSimulationFinished(true);
    };
    simulation.run();
  }, [artworks, artworkSprites, simulationRef.current, worldDimensions]);

  /**
   * Load Artwork Textures from a single spritesheet JSON
   */
  const loadArtworkTextures = useCallback(async () => {
    try {
      // Start loading: set initial progress to 10%
      setLoadingProgress(10);

      // Check if combinedTextures are already loaded
      if (!_.isEmpty(combinedTextures)) {
        console.info(
          "Combined textures already loaded. Skipping texture loading.",
        );
        setTexturesLoaded(true);
        setLoadingProgress(90);
        return;
      }

      const baseUrl =
        "https://museumarnhem.ams3.digitaloceanspaces.com/spritesheet/";
      // Load the main spritesheet JSON
      const mainSpritesheetUrl = `${baseUrl}spritesheet.json`; // Update with your actual URL
      const mainData = await loadJSON(mainSpritesheetUrl);
      setLoadingProgress(20); // Update progress after main JSON is loaded

      if (!mainData.meta || !mainData.meta.related_sheets) {
        console.error("No related_sheets found in main JSON.");
        return;
      }

      const relatedSheets: string[] = mainData.meta.related_sheets;
      const totalSheets = relatedSheets.length;
      let loadedSheets = 0;

      const sheets: any[] = [];

      // Load each related spritesheet sequentially
      for (const sheetUrl of relatedSheets) {
        // Construct the full URL for the related spritesheet
        // Update with your actual base URL
        const fullSheetUrl = `${baseUrl}${sheetUrl}`;

        // Load the spritesheet using PixiJS Assets
        const spritesheet = await Assets.load(fullSheetUrl);
        sheets.push(spritesheet);

        loadedSheets += 1;

        const progressIncrement = 70 / totalSheets;
        setLoadingProgress((prev) =>
          Math.round(Math.min(prev + progressIncrement, 90)),
        );
      }

      // Combine all textures from the loaded spritesheets
      const allTextures: Record<string, Texture> = {};
      for (const sheet of sheets) {
        for (const [name, tex] of Object.entries(sheet.textures)) {
          // @ts-ignore
          allTextures[name] = tex;
        }
      }
      setCombinedTextures(allTextures); // Update the store
      setLoadingProgress(90); // Update progress after combining textures
      setTexturesLoaded(true);
    } catch (error) {
      console.error("Error loading spritesheet:", error);
      // Optionally, you can set progress to 100% and handle the error state
      setLoadingProgress(100);
      setIsLoading(false);
    }
  }, [combinedTextures, setCombinedTextures]);

  const setupSprites = useCallback(async () => {
    if (!combinedTextures) return;
    const artworkSprites = artworks.map((artwork) => {
      const extension = getFileExtension(artwork.imageUrl);
      const ext = extension || "jpg";
      const key = `${artwork.uuid}.${ext}`;
      const texture = combinedTextures[key] || Texture.WHITE;
      return createArtworkSprite(artwork.uuid, texture, false);
    });

    if (artworkSprites.length === 0) {
      console.warn("No valid artworks for sprites found.");
      return;
    }

    setArtworkSprites(artworkSprites);
    setSpritesSetup(true);
  }, [artworks, combinedTextures]);

  const getViewportYearRange = useCallback((): {
    minYear: number;
    maxYear: number;
  } | null => {
    if (!viewportRef.current || !simulationRef.current) return null;

    const viewport = viewportRef.current;
    const bounds = viewport.getVisibleBounds();

    const minX = bounds.x;
    const maxX = bounds.x + bounds.width;

    const xPositions = Object.entries(simulationRef.current.xPositionsByYear)
      .map(([year, x]) => ({ year: Number(year), x: Number(x) }))
      .sort((a, b) => a.x - b.x);

    const minYearObj = xPositions.find((p) => p.x >= minX);
    const minYear = minYearObj
      ? minYearObj.year
      : xPositions[0]?.year || timelineRange.minYear;

    const maxYearObj = [...xPositions].reverse().find((p) => p.x <= maxX);
    const maxYear = maxYearObj
      ? maxYearObj.year
      : xPositions[xPositions.length - 1]?.year || timelineRange.maxYear;

    return { minYear, maxYear };
  }, [timelineRange.minYear, timelineRange.maxYear]);

  const timelineRefs = {
    background: null as Graphics | null,
    axis: null as Graphics | null,
    labels: [] as BitmapText[],
    initialized: false,
    currentValues: { min: 0, mid: 0, max: 0 },
  };

  const addTimeline = useCallback(() => {
    const dynamicRange = getViewportYearRange();
    if (!dynamicRange) {
      timelineContainerRef.current.removeChildren();
      timelineRefs.initialized = false;
      timelineRefs.labels = [];
      return;
    }

    const { minYear, maxYear } = dynamicRange;
    const midpoint = Math.round((minYear + maxYear) / 2);

    const width = dimensions.width;
    const positionY = dimensions.height - 50;
    const tickPositions = [width * 0.05, width * 0.5, width * 0.95];

    if (!timelineRefs.initialized) {
      timelineContainerRef.current.removeChildren();

      const timelineBackground = new Graphics();
      timelineBackground.beginFill(0x000000, 0.5);
      timelineBackground.drawRect(
        0,
        worldDimensions.height - 55,
        worldDimensions.width,
        100,
      );
      timelineBackground.endFill();
      const blurFilter = new BackdropBlurFilter();
      blurFilter.blur = 10;
      timelineBackground.filters = [blurFilter];
      timelineContainerRef.current.addChild(timelineBackground);
      timelineRefs.background = timelineBackground;

      const axisGraphics = new Graphics();
      axisGraphics.lineStyle(2, 0xffffff, 1);
      axisGraphics.moveTo(50, positionY);
      axisGraphics.lineTo(width - 50, positionY);
      timelineContainerRef.current.addChild(axisGraphics);
      timelineRefs.axis = axisGraphics;

      const textStyle = new TextStyle({
        fontSize: 18,
        fill: 0xffffff,
        align: "center",
        fontFamily: "ABC Favorit",
      });

      const minLabel = new BitmapText({
        text: minYear.toString(),
        style: textStyle,
      });
      minLabel.anchor.set(0.5, 0);
      minLabel.position.set(tickPositions[0], positionY + 12);
      timelineContainerRef.current.addChild(minLabel);

      const midLabel = new BitmapText({
        text: midpoint.toString(),
        style: textStyle,
      });
      midLabel.anchor.set(0.5, 0);
      midLabel.position.set(tickPositions[1], positionY + 12);
      timelineContainerRef.current.addChild(midLabel);

      const maxLabel = new BitmapText({
        text: maxYear.toString(),
        style: textStyle,
      });
      maxLabel.anchor.set(0.5, 0);
      maxLabel.position.set(tickPositions[2], positionY + 12);
      timelineContainerRef.current.addChild(maxLabel);

      timelineRefs.labels = [minLabel, midLabel, maxLabel];
      timelineRefs.currentValues = {
        min: minYear,
        mid: midpoint,
        max: maxYear,
      };

      tickPositions.forEach((x) => {
        const tick = new Graphics();
        tick.lineStyle(1, 0xffffff, 1);
        tick.moveTo(x, positionY - 10);
        tick.lineTo(x, positionY + 10);
        timelineContainerRef.current.addChild(tick);
      });

      timelineRefs.initialized = true;
    } else {
      const {
        min: oldMin,
        mid: oldMid,
        max: oldMax,
      } = timelineRefs.currentValues;
      const [minLabel, midLabel, maxLabel] = timelineRefs.labels;
      const duration = 0.5;

      gsap.to(
        { val: oldMin },
        {
          val: minYear,
          duration,
          ease: "power2.out",
          onUpdate: function () {
            minLabel.text = Math.round(this.targets()[0].val).toString();
          },
        },
      );

      gsap.to(
        { val: oldMid },
        {
          val: midpoint,
          duration,
          ease: "power2.out",
          onUpdate: function () {
            midLabel.text = Math.round(this.targets()[0].val).toString();
          },
        },
      );

      gsap.to(
        { val: oldMax },
        {
          val: maxYear,
          duration,
          ease: "power2.out",
          onUpdate: function () {
            maxLabel.text = Math.round(this.targets()[0].val).toString();
          },
        },
      );

      timelineRefs.currentValues = {
        min: minYear,
        mid: midpoint,
        max: maxYear,
      };
    }
  }, [getViewportYearRange, dimensions, worldDimensions]);

  const updateTimeline = useCallback(() => {
    addTimeline();
  }, [addTimeline]);

  const renderPixi = useCallback(async () => {
    if (
      !simulationRef.current ||
      !artworkContainerRef.current ||
      !canvasRef.current
    ) {
      return;
    }
    const simulation = simulationRef.current;
    const pixiApp = new Application();
    PixiPlugin.registerPIXI(pixiApp);
    await pixiApp.init({
      canvas: canvasRef.current,
      width: dimensions.width,
      height: dimensions.height,
      resizeTo: window,
      resolution: window.devicePixelRatio || 1,
      antialias: true,
      autoDensity: true,
      useBackBuffer: true,
      background: "#1A1919",
    });

    pixiAppRef.current = pixiApp;

    if (viewportRef.current) return;
    const viewport = new Viewport({
      screenWidth: dimensions.width,
      screenHeight: dimensions.height,
      worldWidth: worldDimensions.width,
      worldHeight: worldDimensions.height,
      events: pixiApp.renderer.events,
    });

    viewportRef.current = viewport;
    pixiApp.stage.addChild(viewport);

    viewport.on("drag-start", () => {
      pixiApp.canvas.style.cursor = "grabbing";
    });
    viewport.on("drag-end", () => {
      pixiApp.canvas.style.cursor = "grab";
    });

    viewport
      .drag()
      .pinch()
      .wheel()
      .decelerate()
      .clamp({
        direction: "y",
      })
      .bounce({
        sides: "horizontal",
        ease: "easeInOutSine",
      })
      .clampZoom({
        minWidth: (worldDimensions.width / 2) * MIN_ZOOM,
        maxWidth: (worldDimensions.width / 2) * MAX_ZOOM,
        minHeight: (worldDimensions.height / 2) * MIN_ZOOM,
        maxHeight: (worldDimensions.height / 2) * MAX_ZOOM,
        minScale: MIN_ZOOM,
        maxScale: MAX_ZOOM,
      });

    viewport.addChild(artworkContainerRef.current);

    if (isDebugMode) {
      viewport.addChild(debugLayerRef.current);
    }

    simulation.nodes.forEach((node) => {
      const { x, y, sprite } = node;
      addArtworkSpriteEvents(sprite, node, {
        onPointerTap: () => onArtworkTap(sprite, node),
        onSpriteOver: () => onArtworkMouseOver(sprite),
        onSpriteOut: () => onArtworkMouseOut(sprite),
      });
      sprite.x = x;
      sprite.y = y;
      artworkContainerRef.current.addChild(sprite);
    });

    pixiApp.stage.addChild(timelineContainerRef.current);
    addTimeline();

    adjustViewport();

    viewport.on("moved", updateTimeline);
    viewport.on("zoomed", updateTimeline);

    setLoadingProgress(100);
    setIsRendered(true);
    setIsLoading(false);
  }, [
    canvasRef.current,
    simulationRef.current,
    artworkContainerRef.current,
    viewportRef.current,
    addTimeline,
    updateTimeline,
    dimensions,
    worldDimensions,
    isDebugMode,
  ]);

  const adjustViewport = useCallback(() => {
    if (
      !viewportRef.current ||
      !artworkContainerRef.current ||
      !simulationRef.current
    )
      return;
    const viewport = viewportRef.current;

    const bounds = artworkContainerRef.current.getBounds();
    const layoutWidth = bounds.maxX - bounds.minX;
    const layoutHeight = bounds.maxY - bounds.minY;

    const scaleX = worldDimensions.width / layoutWidth;
    const scaleY = worldDimensions.height / layoutHeight;
    const zoom = Math.min(scaleX, scaleY) * 0.9;

    const centerY = (bounds.minY + bounds.maxY) / 2;
    const minX = Math.min(...simulationRef.current.nodes.map((node) => node.x));

    setZoomLevel(zoom);
    viewport.moveCenter(minX, centerY);
    viewport.setZoom(zoom, true);
  }, [
    viewportRef,
    artworkContainerRef,
    simulationRef,
    worldDimensions.width,
    worldDimensions.height,
  ]);

  const onArtworkTap = (sprite: IArtworkSprite, node: any) => {
    const currentTime = Date.now();
    if (currentTime - lastClickTimeRef.current < DOUBLE_CLICK_THRESHOLD) {
      if (isAnimating) {
        lastClickTimeRef.current = currentTime;
        return;
      }
    }
    lastClickTimeRef.current = currentTime;
    setUuid(node.artwork.uuid);
  };

  const onArtworkMouseOver = (sprite: IArtworkSprite) => {
    if (isAnimating) return;
    sprite.filters = [
      new OutlineFilter({ thickness: 1.0, color: 0xffffff, alpha: 0.35 }),
    ];
  };

  const onArtworkMouseOut = (sprite: IArtworkSprite) => {
    if (isAnimating) return;
    sprite.filters = [];
  };

  const animateZoom = (
    viewport: Viewport | null,
    targetZoom: number,
    duration: number = 0.5,
  ) => {
    if (!viewport) return;
    const center = viewport.center;
    gsap.killTweensOf(zoomRef.current);

    gsap.to(zoomRef.current, {
      zoom: targetZoom,
      duration: duration,
      ease: "power2.out",
      onUpdate: () => {
        // @ts-ignore
        viewport.setZoom(zoomRef.current.zoom, { center });
      },
    });
  };

  const zoomRef = useRef({ zoom: zoomLevel });

  useEffect(() => {
    zoomRef.current.zoom = zoomLevel;
  }, [zoomLevel]);

  const debouncedAddTimeline = useCallback(debounce(addTimeline, 100), [
    addTimeline,
  ]);

  const handleZoomLevelChange = () => {
    console.log("Zoom level changed to:", zoomLevel);
    debouncedAddTimeline();
  };

  useEffect(() => {
    return () => {
      debouncedAddTimeline.cancel();
    };
  }, [debouncedAddTimeline]);

  useEffect(() => {
    if (!viewportRef.current) return;

    const viewport = viewportRef.current;

    const onZoomed = () => {
      const currentZoom = viewport.scale.x;
      setZoomLevel(currentZoom);
    };

    viewport.on("zoomed-end", onZoomed);

    return () => {
      viewport.off("zoomed-end", onZoomed);
    };
  }, [viewportRef.current]);

  const handleZoomChange = (value: number) => {
    console.log("handleZoomChange", value);
    const clampedZoom = Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM);

    setZoomLevel(clampedZoom);

    if (viewportRef.current) {
      animateZoom(viewportRef.current, clampedZoom);
    }

    handleZoomLevelChange();
  };

  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(zoomLevel + 0.5, MAX_ZOOM);
    setZoomLevel(newZoom);

    if (viewportRef.current) {
      animateZoom(viewportRef.current, newZoom);
    }

    handleZoomLevelChange();
  }, [zoomLevel]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoomLevel - 0.5, MIN_ZOOM);
    setZoomLevel(newZoom);

    if (viewportRef.current) {
      animateZoom(viewportRef.current, newZoom);
    }
    handleZoomLevelChange();
  }, [zoomLevel]);

  const navigateBack = useCallback(() => {
    navigate("/collection");
  }, [navigate]);

  return (
    <>
      <div className="z-20 h-full w-full absolute top-0 left-0 bg-primary select-none overscroll-none">
        <canvas ref={canvasRef} className="h-full w-full top-0 left-0" />
        <div className="h-full w-full top-0 left-0 z-30">
          <Loading
            isLoading={isLoading}
            progress={loadingProgress}
            message="Tijdlijn laden..."
          />

          {!isLoading && <></>}

          {!isLoading && (
            <ZoomControl
              size={ZoomControlSize.SMALL}
              zoomLevel={zoomLevel}
              onZoomChange={handleZoomChange}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              bottomClass={"bottom-16"}
            />
          )}

          {!isLoading && (
            <div className="top-8 left-4 absolute z-20">
              <BackToCollectionButton onClick={navigateBack} />
            </div>
          )}
        </div>
      </div>
      {uuid && (
        <ArtworkDetailView
          ref={artworkRef}
          uuid={uuid}
          onClose={() => {
            setUuid(null);
          }}
        />
      )}
    </>
  );
};

export default TimelineView;
